import { useEffect, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from 'platform/useMutation';

import {
  UploadDocument,
  UploadDocumentVariables,
} from 'sellDirect/graphql/__generated__/UploadDocument';
import { useObservability } from 'shared/helpers/observability';
import {
  getGraphqlErrorType,
  HelloSignReauthRequiredError,
  showToastForHelloSignError,
} from 'sellDirect/helpers/closingDash/errorHandler';
import { useReauth } from 'platform/components/ReauthContext';

const UploadDocumentMutation = gql`
  mutation UploadDocument(
    $webOfferToken: String!
    $documentName: String!
    $helloSignTaskId: String!
    $s3Key: String!
  ) {
    seller {
      uploadDocument(
        webOfferToken: $webOfferToken
        documentName: $documentName
        helloSignTaskId: $helloSignTaskId
        s3Key: $s3Key
      )
    }
  }
`;

/** TODO: Add documentation */
export const useUploadDocumentToAdmin = () => {
  const observability = useObservability();
  const { showReauth } = useReauth();

  const [mutate, { data, loading, error }] = useMutation<UploadDocument, UploadDocumentVariables>(
    UploadDocumentMutation,
    {
      awaitRefetchQueries: true,
      fetchPolicy: 'no-cache',
      refetchQueries: ['GetSellDirect'],
    },
  );

  const uploadDocumentToAdmin = useCallback(
    async (
      onSuccess: () => void,
      documentName?: string,
      helloSignTaskId?: string,
      s3Key?: string,
      webOfferToken?: string | null,
    ) => {
      if (!webOfferToken) {
        observability.trackError('missing-web-offer-token', undefined, {
          helloSignTaskId,
          s3Key,
          documentName,
        });
        return;
      }
      const result = await mutate({
        variables: {
          documentName: documentName ?? '',
          helloSignTaskId: helloSignTaskId ?? '',
          s3Key: s3Key ?? '',
          webOfferToken,
        },
      }).catch((error) => {
        observability.trackError('upload-document', error, {
          documentName,
          helloSignTaskId,
          s3Key,
        });
      });

      if (result) {
        const errorType = getGraphqlErrorType(result, observability, 'upload-document-to-admin');
        if (errorType === HelloSignReauthRequiredError) {
          showReauth({
            onSuccess: () =>
              uploadDocumentToAdmin(onSuccess, documentName, helloSignTaskId, s3Key, webOfferToken),
            onCancel: () => {},
          });
        } else if (errorType) {
          showToastForHelloSignError(errorType);
        } else {
          // Else no errors, call onSuccess
          onSuccess();
        }
      }
    },
    [observability, mutate, showReauth],
  );

  useEffect(() => {
    if (error) {
      observability.trackError('upload-document', error);
    }
    // eslint-disable-next-line
  }, [error]);

  return { uploadDocumentToAdmin, loading, data, error };
};
